import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const VideoManagerContext = createContext();

export const useVideoManager = () => {
  const context = useContext(VideoManagerContext);
  if (!context) {
    throw new Error('useVideoManager must be used within a VideoManagerProvider');
  }
  return context;
};

export const VideoManagerProvider = ({ children }) => {
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [hoveredVideoId, setHoveredVideoId] = useState(null);
  const videosRef = useRef(new Map());
  const observerRef = useRef(null);

  // ビデオを登録
  const registerVideo = useCallback((id, videoElement) => {
    videosRef.current.set(id, videoElement);
  }, []);

  // ビデオの登録を解除
  const unregisterVideo = useCallback((id) => {
    videosRef.current.delete(id);
    if (activeVideoId === id) {
      setActiveVideoId(null);
    }
    if (hoveredVideoId === id) {
      setHoveredVideoId(null);
    }
  }, [activeVideoId, hoveredVideoId]);

  // アクティブな動画を再生
  const playVideo = useCallback((id, audioEnabled) => {
    console.log('[VideoManager] playVideo called:', { id, audioEnabled });
    
    // すべての動画を一度停止（確実に単一再生を保証）
    videosRef.current.forEach((video, videoId) => {
      if (videoId !== id && !video.paused) {
        video.pause();
      }
    });

    // 新しい動画を再生
    const video = videosRef.current.get(id);
    if (video) {
      video.muted = !audioEnabled;
      console.log('[VideoManager] Setting video.muted to:', !audioEnabled);
      // play()の前に動画の準備状態を確認
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA以上
        video.play().catch(err => {
          console.error('Video play failed:', {
            error: err,
            videoId: id,
            readyState: video.readyState,
            networkState: video.networkState,
            src: video.currentSrc || video.src,
            duration: video.duration,
            buffered: video.buffered.length > 0 ? 
              `${video.buffered.start(0)}-${video.buffered.end(0)}` : 'empty'
          });
        });
      } else {
        // 動画がまだ準備できていない場合は、準備ができてから再生
        console.log('Video not ready, waiting for canplay event');
        const handleCanPlay = () => {
          video.removeEventListener('canplay', handleCanPlay);
          video.play().catch(err => {
            console.error('Video play failed after canplay:', err);
          });
        };
        video.addEventListener('canplay', handleCanPlay);
      }
      setActiveVideoId(id);
    }
  }, []);

  // 動画を停止
  const pauseVideo = useCallback((id) => {
    const video = videosRef.current.get(id);
    if (video) {
      video.pause();
      if (activeVideoId === id) {
        setActiveVideoId(null);
      }
    }
  }, [activeVideoId]);

  // すべての動画を停止
  const pauseAllVideos = useCallback(() => {
    videosRef.current.forEach(video => {
      video.pause();
    });
    setActiveVideoId(null);
  }, []);

  // ホバー状態を設定
  const setVideoHovered = useCallback((id, isHovered) => {
    if (isHovered) {
      setHoveredVideoId(id);
    } else if (hoveredVideoId === id) {
      setHoveredVideoId(null);
    }
  }, [hoveredVideoId]);

  // 音声状態を更新
  const updateAudioState = useCallback((audioEnabled) => {
    videosRef.current.forEach(video => {
      video.muted = !audioEnabled;
    });
  }, []);

  // 動画が再生中かどうかを判定
  const hasActiveVideo = activeVideoId !== null;

  const value = {
    activeVideoId,
    hoveredVideoId,
    hasActiveVideo,
    registerVideo,
    unregisterVideo,
    playVideo,
    pauseVideo,
    pauseAllVideos,
    setVideoHovered,
    updateAudioState,
  };

  return (
    <VideoManagerContext.Provider value={value}>
      {children}
    </VideoManagerContext.Provider>
  );
};
